import React, { useCallback, useMemo, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  styled,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { calcFieldsToDisplay, DEAL_INDEX } from "constants/reports";
import { usePrompt } from "hooks";
import { difference, startCase } from "lodash";

import { State } from "./CreateReportSection";
import { TableColumnsPicker } from "./TableColumnsPicker";
import { TableGroupBy, TableGroupByResult } from "./TableGroupBy";
import { TableIndexNameMenu } from "./TableIndexNameMenu";

interface Props {
  data: State["table"];
  setData: (cb: (prevState: State["table"]) => State["table"]) => void;
}

const Title = styled(Typography)(() => ({
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: "400"
}));

const loading = false;

const StyledChip = styled(Chip)(() => ({
  color: "#071F35",
  fontSize: "12px",
  padding: "4px 8px",
  height: "24px"
}));

const groupByNumber: number = 3;

export const Table: React.FC<Props> = ({ data, setData }) => {
  const { columns, index_name, group_by } = data;

  const openPrompt = usePrompt();

  const [pmeAnchorEl, setPmeAnchorEl] = useState<HTMLDivElement | null>(null);

  const selectedUniqueParameters = useMemo<string[]>(() => {
    return [...difference(columns.calcFields, calcFieldsToDisplay), ...columns.characteristics, ...columns.measures];
  }, [columns]);

  const handleClickCalcFieldChip = useCallback(
    ({ anchorEl, parameter }: { anchorEl: HTMLDivElement | null; parameter: string }) => {
      if (parameter === "PME") {
        return setPmeAnchorEl(anchorEl);
      }

      setData((prevState) => ({
        ...prevState,
        columns: {
          ...prevState.columns,
          calcFields: prevState.columns.calcFields.includes(parameter)
            ? prevState.columns.calcFields.filter((item) => item !== parameter)
            : [...prevState.columns.calcFields, parameter]
        }
      }));
    },
    [columns]
  );

  const handleSelectIndex = useCallback((value: State["table"]["index_name"]) => {
    setPmeAnchorEl(null);

    if (!value) {
      setData((prev) => ({
        ...prev,

        columns: {
          ...prev.columns,
          calcFields: prev.columns.calcFields.filter((item) => item !== "PME")
        }
      }));
    } else if (value.index === DEAL_INDEX) {
      setData((prev) => ({
        ...prev,
        columns: {
          ...prev.columns,
          calcFields: [...(prev.columns.calcFields || []), "PME"]
        },
        index_name: null
      }));
    } else {
      setData((prev) => ({
        ...prev,
        columns: {
          ...prev.columns,
          calcFields: [...(prev.columns.calcFields || []), "PME"]
        },
        index_name: value
      }));
    }
  }, []);

  const handlePickTableColumns = async (anchorEl: HTMLButtonElement) => {
    const newColumnsSet = await openPrompt({
      Component: TableColumnsPicker,
      componentType: "popover",
      anchorEl,
      props: {
        selectedColumns: {
          ...columns,
          calcFields: difference(columns.calcFields, calcFieldsToDisplay)
        }
      }
    });

    if (!newColumnsSet) return;

    setData((prevState) => ({
      ...prevState,
      columns: {
        characteristics: newColumnsSet.characteristics,
        measures: newColumnsSet.measures,
        calcFields: [
          ...calcFieldsToDisplay.filter((field) => prevState.columns.calcFields.includes(field)),
          ...newColumnsSet.calcFields
        ]
      }
    }));
  };

  const handleGroupBy = async (anchorEl: HTMLDivElement) => {
    const result = await openPrompt({
      Component: TableGroupBy,
      componentType: "popover",
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      anchorEl,
      props: {
        groupBy: group_by,
        columns,
        groupsNumber: groupByNumber
      }
    });

    if (!result) return;

    const { groupBy: newGroupBy, measures: newMeasures, calcFields: newCalcFields } = result as TableGroupByResult;
    setData((prevState) => ({
      ...prevState,
      group_by: newGroupBy,
      columns: {
        ...prevState.columns,
        measures: newMeasures,
        calcFields: newCalcFields
      }
    }));
  };

  const groupByChips = useMemo(() => {
    return group_by.length < groupByNumber
      ? [...group_by, ...Array(groupByNumber - group_by.length).fill("None")]
      : group_by;
  }, [group_by]);

  return (
    <>
      <Stack sx={{ gap: "14px", width: 310 }}>
        <Stack sx={{ gap: "8px" }}>
          <Title>Table Name</Title>
          <TextField value={data.name} onChange={(e) => setData((prev) => ({ ...prev, name: e.target.value }))} />
        </Stack>
        <Stack sx={{ gap: "8px" }}>
          <Title>Parameters</Title>
          <Stack sx={{ flexDirection: "row", gap: "10px", flexWrap: "wrap" }}>
            {calcFieldsToDisplay.map((parameter) => {
              return (
                <StyledChip
                  key={parameter}
                  onClick={(e) => handleClickCalcFieldChip({ anchorEl: e.currentTarget, parameter })}
                  label={parameter}
                  sx={{
                    ...(columns.calcFields.includes(parameter) && {
                      backgroundColor: "#c3c9cf !important"
                    })
                  }}
                />
              );
            })}
            {Boolean(selectedUniqueParameters.length) && (
              <StyledChip
                label={`(${selectedUniqueParameters.length})`}
                sx={{
                  cursor: "default",
                  backgroundColor: "#c3c9cf !important"
                }}
              />
            )}
            {loading ? (
              <CircularProgress size="20px" />
            ) : (
              <Button
                variant="text"
                size="sm"
                sx={{ color: "#26A6AE", fontSize: "14px" }}
                onClick={(e) => handlePickTableColumns(e.currentTarget)}
              >
                More
              </Button>
            )}
          </Stack>
        </Stack>
        <Stack sx={{ gap: "8px" }}>
          <Title>Group by</Title>
          <Stack sx={{ flexDirection: "row", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            {groupByChips.map((item, index) => (
              <Stack key={index} sx={{ flexDirection: "row", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <StyledChip
                  onClick={(e) => handleGroupBy(e.currentTarget)}
                  label={item === "None" ? item : startCase(item)}
                />
                {index < groupByChips.length - 1 && <ArrowForwardIosIcon sx={{ fontSize: "14px" }} />}
              </Stack>
            ))}
          </Stack>
        </Stack>
        <FormControlLabel
          control={<Switch />}
          checked={data.original_currency}
          onChange={(_, checked) => setData((prev) => ({ ...prev, original_currency: checked }))}
          label="Original Currency"
          labelPlacement="start"
          sx={{
            width: "100%",
            margin: 0,
            "& .MuiFormControlLabel-label": {
              flex: 1
            }
          }}
        />
      </Stack>
      <TableIndexNameMenu
        anchorEl={pmeAnchorEl}
        selectedIndex={index_name}
        calculatedFields={columns.calcFields}
        onClose={() => setPmeAnchorEl(null)}
        onSelectIndex={handleSelectIndex}
      />
    </>
  );
};
